import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Button, 
  Container, 
  Box, 
  Typography, 
  TextField,
  Divider,
  Alert,
  Link,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  ThemeProvider,
  createTheme
} from '@mui/material'
import { Google as GoogleIcon, Visibility, VisibilityOff } from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import '../styles/LoginForm.css'

// Create a custom theme just for the login form
const loginTheme = createTheme({
  palette: {
    primary: {
      main: '#8A42EC',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '20px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#8A42EC',
            },
            '&:hover fieldset': {
              borderColor: '#6200ee',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6200ee',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#8A42EC',
          },
          '& .MuiInputBase-input': {
            height: '20px',
            padding: '15px',
          },
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '16px',
        }
      }
    }
  }
});

export const Login = () => {
  const { signInWithGoogle, signInWithEmail, resetPassword, error: authError, setError } = useAuth()
  const navigate = useNavigate()
  const [isResetPassword, setIsResetPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleEmailAuth = async (e) => {
    e.preventDefault()
    setLoading(true)
    setFormError('')
    setError(null)

    try {
      if (isResetPassword) {
        await resetPassword(email)
        setFormError('Password reset email sent. Please check your inbox.')
        setLoading(false)
        return
      }

      const result = await signInWithEmail(email, password)
      console.log('[Login] signInWithEmail result:', result)
      if (result && result.user) {
        // Redirect to root for consistency with Google Auth
        window.location.href = '/';
      } else {
        setFormError('Login failed: No user returned');
      }
      
    } catch (err) {
      console.error('Authentication error:', err)
      setFormError(err.message)
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true)
      setFormError('')
      setError(null)
      await signInWithGoogle()
      
      // Navigate to root after successful Google login
      navigate('/')
      
    } catch (err) {
      console.error('Google sign in error:', err)
      setFormError('Failed to sign in with Google')
      setLoading(false)
    }
  }

  const toggleResetPassword = () => {
    setIsResetPassword(!isResetPassword)
    setFormError('')
    setError(null)
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ThemeProvider theme={loginTheme}>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h4" sx={{ mb: 4, color: '#8A42EC', textAlign: 'center' }}>
              Welcome to Pulse
            </Typography>

            {(formError || authError) && (
              <Alert 
                severity={formError.includes('check your email') ? 'success' : 'error'} 
                sx={{ mb: 2, width: '100%' }}
              >
                {formError || authError}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              disabled={loading}
              sx={{ mb: 2, height: '50px' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign in with Google'}
            </Button>

            <Divider sx={{ width: '100%', mb: 2 }}>OR</Divider>

            {/* Basic HTML form that always works */}
            <form onSubmit={handleEmailAuth} className="login-form basic-form">
              <div className="form-group">
                <label htmlFor="email">Email Address <span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
              
              {!isResetPassword && (
                <div className="form-group">
                  <label htmlFor="password">Password <span className="required">*</span></label>
                  <div className="password-field">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={handleClickShowPassword}
                    >
                      {showPassword ? '👁‍🗨' : '👁'}
                    </button>
                  </div>
                </div>
              )}
              
              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? 
                  "Loading..." : 
                  isResetPassword ? 
                    'Send Reset Link' : 
                    'Sign In'
                }
              </button>
              
              <div className="form-links">
                {!isResetPassword && (
                  <button
                    type="button"
                    className="text-link"
                    onClick={toggleResetPassword}
                  >
                    {isResetPassword ? 'Back to Sign In' : 'Forgot Password?'}
                  </button>
                )}
              </div>
            </form>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  )
} 